/**
 * TanStack Query hooks for the read endpoints this pass needs. Ticket
 * lifecycle mutation hooks (resolve, confirm, reopen, ...) belong to the
 * ticket-detail screens pass, not the foundation — they need the ETag/
 * `If-Match` plumbing from `client.ts`, but wiring specific forms to them is
 * out of scope here.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AssignTicketDto,
  CancelTicketDto,
  CloseTicketDto,
  ClosureRecipientsDto,
  CommentDto,
  ConfirmResolutionDto,
  CreateCommentDto,
  EscalateTicketDto,
  PaginatedDto,
  PendTicketDto,
  ReopenTicketDto,
  ResolveTicketDto,
  RestoreTicketDto,
  TicketCountsDto,
  TicketDetailDto,
  TicketListItemDto,
  TicketListQueryDto,
  TicketStatus,
  TriageTicketDto,
  UserConfirmationStatus,
} from '@ithelpdesk/shared';
import { apiGet, apiGetWithEtag, apiPost, apiPostVersioned, shouldRetryQuery, NO_MUTATION_RETRY, type RawResponse } from './client';
import { observeCounts } from '../features/live/liveCounts';
import { usePageVisible } from './notifications';

/**
 * Central key factory so every query and every cache invalidation refers to
 * the same key shape. `list` keys on the full filter object (including
 * `cursor`) so each distinct filter/cursor combination caches independently,
 * matching the keyset-pagination model in docs/API.md §3.
 */
export const queryKeys = {
  me: () => ['me'] as const,
  tickets: {
    all: () => ['tickets'] as const,
    lists: () => ['tickets', 'list'] as const,
    list: (params: TicketListQueryParams) => ['tickets', 'list', params] as const,
    details: () => ['tickets', 'detail'] as const,
    detail: (id: string) => ['tickets', 'detail', id] as const,
    counts: () => ['tickets', 'counts'] as const,
  },
};

export type TicketListQueryParams = Partial<Omit<TicketListQueryDto, 'status'>> & {
  status?: TicketListQueryDto['status'];
};

function toSearchParams(params: TicketListQueryParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else if (value instanceof Date) {
      search.set(key, value.toISOString());
    } else {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

/** `GET /tickets` — scoped, cursor-paginated list. The visibility scope itself is enforced server-side; this hook has no way to widen it. */
export function useTicketList(params: TicketListQueryParams = {}, options: { enabled?: boolean; keepPrevious?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.tickets.list(params),
    queryFn: ({ signal }) => apiGet<PaginatedDto<TicketListItemDto>>(`/tickets${toSearchParams(params)}`, { signal }),
    retry: shouldRetryQuery,
    enabled: options.enabled ?? true,
    ...(options.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

/**
 * `GET /tickets/:id` — detail, with its `ETag`. The result's `data.etag` is
 * what a later mutation on this same screen must pass back as `ifMatch`; do
 * not re-derive a version number from `TicketDetailDto.version` for that
 * purpose; the header is the one the server will actually re-check.
 */
export function useTicketDetail(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.tickets.detail(id ?? ''),
    queryFn: ({ signal }): Promise<RawResponse<TicketDetailDto>> => apiGetWithEtag<TicketDetailDto>(`/tickets/${id}`, { signal }),
    enabled: Boolean(id),
    retry: shouldRetryQuery,
  });
}

/** How often the counts are re-read while the tab is visible, so "new" / "assigned to me" stay fresh with no reload. */
export const COUNTS_POLL_INTERVAL_MS = 30_000;

/**
 * `GET /tickets/counts` — dashboard aggregates, reusing the same scoped query path as the list (docs/API.md §3).
 * Polled while the tab is visible and refetched on focus. Every value comes from the server; the query
 * function only reports what it received to `observeCounts`, which detects genuine increases (animation
 * + chime) before the cache is updated.
 */
export function useTicketCounts() {
  const visible = usePageVisible();
  return useQuery({
    queryKey: queryKeys.tickets.counts(),
    queryFn: async ({ signal }) => {
      const counts = await apiGet<TicketCountsDto>('/tickets/counts', { signal });
      observeCounts(queryKeys.tickets.counts().join('/'), {
        new: (counts.byStatus?.NEW ?? 0) + (counts.byStatus?.TRIAGED ?? 0),
        unassigned: counts.unassigned,
        assignedToMe: counts.assignedToMe,
      });
      return counts;
    },
    retry: shouldRetryQuery,
    refetchInterval: visible ? COUNTS_POLL_INTERVAL_MS : false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

// ---------------------------------------------------------------------------
// Ticket lifecycle mutations — added for the ticket-detail screens pass.
//
// Every lifecycle endpoint (docs/API.md §4) hands back only a thin outcome —
// `{ ticketId, status, version }`, or a couple of extra fields for `resolve`
// and `confirm-resolution` — never the full `{ ticket, resolutionAttempts,
// comments, version }` shape `GET /tickets/:id` returns. So rather than try to
// hand-patch the detail cache from a thin response, every hook here just
// invalidates the detail query (plus the list/counts queries other screens
// read) on success and lets the next `GET /tickets/:id` bring back the fresh
// ETag this screen's next mutation must send as `If-Match`. This is also why
// there is deliberately no cache-patching helper that reads a mutation
// response's `version` and writes it into the detail cache directly — doing
// that without the rest of the fields would desync the cached ticket from
// what the server actually holds.
// ---------------------------------------------------------------------------

/** The shape shared by every "simple" transition endpoint (assign, triage, start, resume, pend, reopen, close). */
export interface LifecycleMutationResult {
  ticketId: string;
  status: TicketStatus;
  version: number;
}

/** `POST /tickets/:id/resolve`'s own richer outcome — the new attempt's id and number, not just a status. */
export interface ResolveMutationResult {
  ticketId: string;
  ticketNumber: string;
  resolutionId: string;
  attemptNo: number;
  version: number;
}

/** `POST /tickets/:id/confirm-resolution`'s own outcome. */
export interface ConfirmResolutionMutationResult {
  ticketId: string;
  ticketNumber: string;
  status: TicketStatus;
  userConfirmationStatus: UserConfirmationStatus;
  version: number;
}

function invalidateTicketDetail(queryClient: QueryClient, id: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(id) });
  // A lifecycle change on this ticket can move it in or out of any queue
  // (My Tickets, Unassigned, Awaiting Confirmation, ...) and change the
  // dashboard's counts — both need a refetch too, not just this detail page.
  void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.lists() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.counts() });
}

export interface VersionedMutationVars<TBody> {
  body: TBody;
  /** The ETag captured from the ticket's last `GET` — see `useTicketDetail`. */
  ifMatch: string;
  /** Required by docs/API.md §2 on `/resolve`; harmless (and unused server-side) elsewhere. */
  idempotencyKey?: string;
}

/**
 * One `useMutation` shape shared by every versioned lifecycle endpoint. Each
 * exported hook below is a thin, specifically-typed wrapper over this rather
 * than a single generic "send an intent" hook — there is deliberately no
 * `{status: ...}` body anywhere in this file, matching docs/API.md §1's rule
 * that no request ever names a target status; every wrapper posts to its own
 * fixed, named route.
 */
function useVersionedTicketMutation<TBody, TResult>(id: string, path: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, ifMatch, idempotencyKey }: VersionedMutationVars<TBody>) =>
      apiPostVersioned<TResult>(`/tickets/${id}${path}`, body, ifMatch, idempotencyKey ? { idempotencyKey } : {}),
    onSuccess: () => invalidateTicketDetail(queryClient, id),
    retry: NO_MUTATION_RETRY,
  });
}

/**
 * `POST /tickets/:id/resolve` — records the resolution AND closes the ticket in one server
 * transaction (hotfix 27). The requester can still reopen it. The response's `version` is the
 * post-close version.
 */
export function useResolveTicketMutation(id: string) {
  return useVersionedTicketMutation<ResolveTicketDto, ResolveMutationResult>(id, '/resolve');
}

/** The requester's verdict — CONFIRMED closes (subject to the server's own guards), REJECTED reopens. Never callable on behalf of another actor; the server derives the actor from the session. */
export function useConfirmResolutionMutation(id: string) {
  return useVersionedTicketMutation<ConfirmResolutionDto, ConfirmResolutionMutationResult>(id, '/confirm-resolution');
}

/** Reopens a CLOSED ticket (distinct from rejecting a resolution, which reopens a RESOLVED one via confirm-resolution). */
export function useReopenTicketMutation(id: string) {
  return useVersionedTicketMutation<ReopenTicketDto, LifecycleMutationResult>(id, '/reopen');
}

/** Administrative override close only — the route itself is gated on `ticket:close:override`, which no technician role holds. */
export function useCloseTicketOverrideMutation(id: string) {
  return useVersionedTicketMutation<CloseTicketDto, LifecycleMutationResult>(id, '/close');
}

/** Handles both the ASSIGN and REASSIGN intents — the server picks whichever applies from the ticket's current status. */
export function useAssignTicketMutation(id: string) {
  return useVersionedTicketMutation<AssignTicketDto, LifecycleMutationResult>(id, '/assign');
}

export function useTriageTicketMutation(id: string) {
  return useVersionedTicketMutation<TriageTicketDto, LifecycleMutationResult>(id, '/triage');
}

export function usePendTicketMutation(id: string) {
  return useVersionedTicketMutation<PendTicketDto, LifecycleMutationResult>(id, '/pend');
}

/** No request body. */
export function useStartWorkMutation(id: string) {
  return useVersionedTicketMutation<Record<string, never>, LifecycleMutationResult>(id, '/start');
}

/** No request body. */
export function useResumeWorkMutation(id: string) {
  return useVersionedTicketMutation<Record<string, never>, LifecycleMutationResult>(id, '/resume');
}

/** Two eligible actors, one route — see `TicketWorkflowService.cancel`'s doc comment. */
export function useCancelTicketMutation(id: string) {
  return useVersionedTicketMutation<CancelTicketDto, LifecycleMutationResult>(id, '/cancel');
}

/** Administrative undo of a cancellation only — `ticket:admin:restore`, ADMIN only. */
export function useRestoreTicketMutation(id: string) {
  return useVersionedTicketMutation<RestoreTicketDto, LifecycleMutationResult>(id, '/restore');
}

/** NOT a status transition — an audited flag/reassignment. See `TicketWorkflowService.escalate`. */
export function useEscalateTicketMutation(id: string) {
  return useVersionedTicketMutation<EscalateTicketDto, LifecycleMutationResult>(id, '/escalate');
}

/**
 * `GET /tickets/:id/closure-recipients` — whether the Email Deletion CC section applies to this
 * ticket and what the locked/optional recipients are. Pass `enabled` only while the resolve form
 * is open; the server answers 403 without `ticket:resolve`. Always refetched on open so an admin's
 * change to the mandatory CC is seen.
 */
export function useClosureRecipients(ticketId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['tickets', 'closure-recipients', ticketId] as const,
    queryFn: ({ signal }) => apiGet<ClosureRecipientsDto>(`/tickets/${ticketId}/closure-recipients`, { signal }),
    retry: shouldRetryQuery,
    staleTime: 0,
    gcTime: 0,
    enabled,
  });
}

/**
 * `POST /tickets/:id/comments` — NOT gated by `If-Match`/version per
 * docs/API.md §5 (a comment does not move the ticket through the state
 * machine), but `Idempotency-Key` is required per §2. The route is
 * implemented (`ticket.controller.ts#createComment`, delegating to
 * `CommentService`) — the ticket-detail screens pass consumes it directly.
 */
export function useCreateTicketCommentMutation(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: CreateCommentDto; idempotencyKey: string }) =>
      apiPost<CommentDto>(`/tickets/${id}/comments`, body, { idempotencyKey }),
    onSuccess: () => invalidateTicketDetail(queryClient, id),
    retry: NO_MUTATION_RETRY,
  });
}

// ---------------------------------------------------------------------------
// Catalog — read-only picklists (docs/API.md §8) for the Triage and Assign
// forms on the ticket-detail screen. Content-derived weak ETags make these
// cheap to poll on every modal open, so a fairly long `staleTime` is enough;
// there is no mutation anywhere in this app that changes catalog data (that's
// the admin app's job), so a stale list for a few minutes is harmless.
// ---------------------------------------------------------------------------

// Short, so a category/team added by an admin reaches other users' open pages within seconds (they refetch on focus/navigation).
const CATALOG_STALE_TIME = 15_000;

export interface CatalogSubcategoryDto {
  id: string;
  code: string;
  name: string;
}

export interface CatalogCategoryDto {
  id: string;
  code: string;
  name: string;
  subcategories: CatalogSubcategoryDto[];
}

/** Shared shape of `/catalog/priorities`, `/catalog/impacts`, `/catalog/urgencies`. */
export interface CatalogRankedRefDto {
  id: string;
  code: string;
  name: string;
  rank: number;
}

export interface CatalogTeamDto {
  id: string;
  code: string;
  name: string;
}

export interface CatalogTechnicianDto {
  id: string;
  displayName: string;
  email: string;
}

export const catalogKeys = {
  categories: () => ['catalog', 'categories'] as const,
  priorities: () => ['catalog', 'priorities'] as const,
  impacts: () => ['catalog', 'impacts'] as const,
  urgencies: () => ['catalog', 'urgencies'] as const,
  teams: () => ['catalog', 'teams'] as const,
  technicians: () => ['catalog', 'technicians'] as const,
  resolutionCategories: () => ['catalog', 'resolution-categories'] as const,
};

export interface CatalogResolutionCategoryDto {
  id: string;
  code: string;
  name: string;
}

/**
 * Active resolution categories for the caller's org. Server-gated on
 * `ticket:resolve` — pass `enabled: false` for a caller who cannot resolve.
 */
export function useCatalogResolutionCategories(enabled = true) {
  return useQuery({
    queryKey: catalogKeys.resolutionCategories(),
    queryFn: ({ signal }) => apiGet<CatalogResolutionCategoryDto[]>('/catalog/resolution-categories', { signal }),
    retry: shouldRetryQuery,
    staleTime: CATALOG_STALE_TIME,
    enabled,
  });
}

export function useCatalogCategories() {
  return useQuery({
    queryKey: catalogKeys.categories(),
    queryFn: ({ signal }) => apiGet<CatalogCategoryDto[]>('/catalog/categories', { signal }),
    retry: shouldRetryQuery,
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useCatalogPriorities() {
  return useQuery({
    queryKey: catalogKeys.priorities(),
    queryFn: ({ signal }) => apiGet<CatalogRankedRefDto[]>('/catalog/priorities', { signal }),
    retry: shouldRetryQuery,
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useCatalogImpacts() {
  return useQuery({
    queryKey: catalogKeys.impacts(),
    queryFn: ({ signal }) => apiGet<CatalogRankedRefDto[]>('/catalog/impacts', { signal }),
    retry: shouldRetryQuery,
    staleTime: CATALOG_STALE_TIME,
  });
}

export function useCatalogUrgencies() {
  return useQuery({
    queryKey: catalogKeys.urgencies(),
    queryFn: ({ signal }) => apiGet<CatalogRankedRefDto[]>('/catalog/urgencies', { signal }),
    retry: shouldRetryQuery,
    staleTime: CATALOG_STALE_TIME,
  });
}

/** Staff-only server-side (`ticket:read:team`/`ticket:read:any`) — pass `enabled: false` for a caller known not to hold either. */
export function useCatalogTeams(enabled = true) {
  return useQuery({
    queryKey: catalogKeys.teams(),
    queryFn: ({ signal }) => apiGet<CatalogTeamDto[]>('/catalog/teams', { signal }),
    retry: shouldRetryQuery,
    staleTime: CATALOG_STALE_TIME,
    enabled,
  });
}

/** Staff-only server-side, same gate as `useCatalogTeams`. */
export function useCatalogTechnicians(enabled = true) {
  return useQuery({
    queryKey: catalogKeys.technicians(),
    queryFn: ({ signal }) => apiGet<CatalogTechnicianDto[]>('/catalog/technicians', { signal }),
    retry: shouldRetryQuery,
    staleTime: CATALOG_STALE_TIME,
    enabled,
  });
}
